import { ContextualMessage, Stack, T } from "@bbnpm/bb-ui-framework";
import { useEffect, useState } from "react";
import "./help.min.css";

const Help = () => {
    const [selected, setSelected] = useState();

    useEffect(() => {
        const links = document.querySelectorAll(".page-nav li");
        links.forEach((link) => link.classList.remove("selected"));
        selected && selected.classList.add("selected");
    }, [selected]);

    // subnav
    useEffect(() => {
        const mainContent = document.querySelector(".main-content");
        const links = document.querySelectorAll(".page-nav li");
        setSelected(links[0]);

        for (let i = 0; i < links.length; i++) {
            links[i].addEventListener("click", (e) => {
                const scrollToElm = document.getElementById(e.currentTarget.getAttribute("linkto"));

                mainContent.scrollTo({
                    top: scrollToElm.offsetTop - 60 - 10, // nav height = 60, 10 = same space from header as subnav
                    behavior: "smooth",
                });
            });
        }

        const currRoute = window.location.href;
        const currSection = currRoute.split("#")[1];
        const loc = document.querySelector(`[linkto="${currSection}"]`);
        if (currSection && loc) loc.click();
    }, []);

    // intersection observer
    useEffect(() => {
        let scrollDirection = "down";
        let prevYPosition = 0;
        const links = document.querySelectorAll(".page-nav li");
        const header = document.querySelector("header.bbui-titlebar");
        const targets = document.querySelectorAll(".help-section-title");
        targets.forEach((target, idx) => (target["data-id"] = idx));

        const options = {
            root: document.querySelector("#root"),
            rootMargin: `${(header.offsetHeight + targets[0].offsetHeight * 2) * -1}px`,
            threshold: 0, // when out of view
        };

        const setScrollDirection = () => {
            // .main-content is AppLayout.Main, the scroll container under kind="panel".
            const scrollRoot = document.querySelector(".main-content");
            scrollDirection = scrollRoot.scrollTop >= prevYPosition ? "down" : "up";
            prevYPosition = scrollRoot.scrollTop;
        };

        const handleIntersection = (entries) => {
            entries.forEach((entry) => {
                setScrollDirection();
                const sectionId = entry.target["data-id"];

                if (scrollDirection === "down") {
                    if (
                        (!entry.isIntersecting || sectionId === 0) && // invert conditional because threshold 0
                        entry.boundingClientRect.top < 100
                    ) {
                        // ignore events that occur far from top
                        setSelected(links[sectionId]);
                    }
                } else {
                    if (entry.isIntersecting) {
                        // check for visibility when scrolling back up
                        links[sectionId].previousElementSibling &&
                            setSelected(links[sectionId].previousElementSibling);
                    }
                }
            });
        };

        const observer = new IntersectionObserver(handleIntersection, options);
        targets.forEach((target) => observer.observe(target));

        return () => observer.disconnect();
    }, []);

    return (
        <div className="help">
            <div className="help-body">
                <div className="page-nav">
                    <ul>
                        <li linkto="section_contact" className="selected">
                            Contact Us
                        </li>
                        <li linkto="section_windows">Windows Help</li>
                        <li linkto="section_mac">Mac Help</li>
                        <li linkto="section_linux">Linux Help</li>
                        <li linkto="section_faq">Frequently Asked Questions</li>
                        <li linkto="section_bunit">B-Unit enrollment</li>
                        <li linkto="section_files">Accessing Local Files</li>
                        <li linkto="section_excel">Bloomberg Excel Add-In</li>
                        <li linkto="section_html5">Launch with Browser</li>
                        <a id="downloads" href="/downloads" target="_blank">
                            <li>Downloads</li>
                        </a>
                    </ul>
                </div>
                <div className="content">
                    <T.H1 className="page-title">BBA Help</T.H1>
                    <div id="section_contact" className="help-section">
                        <T.H2 as="div" className="help-section-title">
                            Contact us
                        </T.H2>
                        <T.BodyLarge as="div" className="help-section-content contact-section">
                            <div className="group sub_section_group">
                                Not a Terminal user?{" "}
                                <a
                                    className="font-medium"
                                    href="https://www.bloomberg.com/professional/contact-menu/?utm_source=bba-login&amp;bbgsum=DG-WS-PROF-bba-login"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    Request a demo.
                                </a>
                            </div>

                            <div className="group sub_section_group">
                                <T.BodyLargeBold as="strong">The Americas</T.BodyLargeBold>
                                <div>+1 212 318 2000</div>
                            </div>

                            <div className="group sub_section_group">
                                <T.BodyLargeBold as="strong">
                                    Europe, Middle East, &amp; Africa
                                </T.BodyLargeBold>
                                <div>+44 20 7330 7500</div>
                            </div>

                            <div className="group sub_section_group">
                                <T.BodyLargeBold as="strong">Asia Pacific</T.BodyLargeBold>
                                <div>+65 6212 1000</div>
                            </div>

                            <div className="group">
                                <a href="https://www.bloomberg.com/faq/question/how-can-i-contact-bloomberg-for-support">
                                    See complete list
                                </a>
                            </div>
                        </T.BodyLarge>
                    </div>

                    <div id="section_windows" className="help-section">
                        <T.H2 as="div" className="help-section-title">
                            Windows
                        </T.H2>
                        <T.BodyLarge as="div" className="help-section-content">
                            <T.H3 as="h1">System Requirements</T.H3>
                            <div className="sub-section-content">
                                <div className="group sub-section-group">
                                    <div>Windows 10 or later, Server 2019 or later</div>
                                    <div>
                                        Citrix Workspace App, which can be installed{" "}
                                        <a href="/downloads" target="_blank">
                                            here
                                        </a>
                                    </div>
                                </div>
                            </div>
                            <T.H3 as="h1">Accessing files on your local computer</T.H3>
                            <div className="group question-pair">
                                <T.H4 as="h2" id="drive_access_windows">
                                    How do I access files on my Windows PC?
                                </T.H4>
                                <div>
                                    <ol className="numeric-list">
                                        <li>
                                            <span>
                                                Locate and then right-click on the Citrix Workspace
                                                App icon in your System Tray.
                                            </span>
                                            <div>
                                                <img
                                                    src={
                                                        process.env.PUBLIC_URL + "/img/image001.png"
                                                    }
                                                    alt=""
                                                />
                                            </div>
                                            <div>
                                                <ol className="alpha-list">
                                                    <li>
                                                        <span>
                                                            Note: You may need to click the arrow
                                                            near the date and time in the System
                                                            Tray to locate the Citrix Workspace App
                                                            icon.
                                                        </span>
                                                        <div>
                                                            <img
                                                                src={
                                                                    process.env.PUBLIC_URL +
                                                                    "/img/image003.png"
                                                                }
                                                                alt=""
                                                            />
                                                        </div>
                                                    </li>
                                                </ol>
                                            </div>
                                        </li>

                                        <li>
                                            <span>
                                                Allow access to your computer's file system by using
                                                the Citrix Connection Center
                                            </span>

                                            <div>
                                                <ol className="alpha-list">
                                                    <li>
                                                        <span>
                                                            From the Citrix Connection Center Dialog
                                                            Box, click on the server to which you
                                                            are connected.
                                                        </span>
                                                        <ul>
                                                            <li>
                                                                The name of the server usually
                                                                begins with OBBBA or NJBBA. In this
                                                                example the server name is
                                                                NJBBA2-101-026
                                                            </li>
                                                        </ul>
                                                    </li>

                                                    <li>
                                                        <span>
                                                            After selecting the server name then
                                                            select the Preferences icon.
                                                        </span>
                                                        <div>
                                                            <img
                                                                src={
                                                                    process.env.PUBLIC_URL +
                                                                    "/img/image005.png"
                                                                }
                                                                alt=""
                                                            />
                                                        </div>
                                                    </li>

                                                    <li>
                                                        <span>
                                                            Select the appropiate file level access
                                                            to your computer's file system.
                                                        </span>
                                                        <ContextualMessage kind="warning">
                                                            Note that "Read and Write" grants
                                                            permission to save files to your
                                                            computer’s file system.
                                                        </ContextualMessage>
                                                        <div>
                                                            <img
                                                                src={
                                                                    process.env.PUBLIC_URL +
                                                                    "/img/image007.png"
                                                                }
                                                                alt=""
                                                            />
                                                        </div>
                                                    </li>
                                                </ol>
                                            </div>
                                        </li>
                                    </ol>
                                </div>
                            </div>
                            <T.H3 as="h1"> Bloomberg Software Installation for Windows</T.H3>
                            <div className="sub-section-content">
                                <a
                                    className="white-link underline-link"
                                    href="https://www.bloomberg.com/professional/downloads/"
                                >
                                    Download
                                </a>{" "}
                                and install the full Windows version of the Bloomberg Terminal on
                                your PC.
                            </div>
                        </T.BodyLarge>
                    </div>

                    <div id="section_mac" className="help-section">
                        <T.H2 as="div" className="help-section-title">
                            Mac
                        </T.H2>
                        <T.BodyLarge as="div" className="help-section-content">
                            <T.H3 as="h1">System Requirements</T.H3>
                            <div className="sub-section-content">
                                <div className="group sub-section-group">
                                    <div>MacOS 11 (Big Sur) or later</div>
                                    <div>
                                        Citrix Workspace App, which can be installed{" "}
                                        <a href="/downloads" target="_blank">
                                            here
                                        </a>
                                    </div>
                                </div>
                            </div>
                            <div className="group question-pair">
                                <T.H4 as="h2" id="drive_access_mac">
                                    How do I access files on my Mac?
                                </T.H4>
                                <div>
                                    <div>
                                        <ContextualMessage kind="warning">
                                            NOTE: Changes to your local drive access permissions
                                            will not take effect when they are made while you are
                                            logged into BBA. Changes will only take effect on logons
                                            AFTER you have made a change to your local drive access
                                            permissions
                                        </ContextualMessage>
                                    </div>
                                    <ol className="numeric-list">
                                        <li>
                                            <span>
                                                Ensure that you are logged out of Bloomberg
                                                Anywhere. Once logged out, locate the Citrix
                                                Workspace App icon on the menu bar. Right click on
                                                the Citrix Workspace App icon and select
                                                Preferences.
                                            </span>
                                            <div>
                                                <img
                                                    src={
                                                        process.env.PUBLIC_URL +
                                                        "/img/image001-mac.png"
                                                    }
                                                    alt=""
                                                />
                                            </div>
                                        </li>

                                        <li>
                                            <span>
                                                Select the File Access icon and configure the
                                                appropriate access to your drives. Select “Read and
                                                Write” to enable the ability to save files to your
                                                hard drive.
                                            </span>
                                            <div>
                                                <img
                                                    src={
                                                        process.env.PUBLIC_URL +
                                                        "/img/image071-mac.png"
                                                    }
                                                    alt=""
                                                />
                                            </div>
                                        </li>
                                        <li>
                                            <span>
                                                Once logged in to Bloomberg Anywhere you can now
                                                access the drives on your Mac. This can be confirmed
                                                by running PFM and selecting the Upload File option.
                                                On the left side below Folders, expand the Shared
                                                Drives option. The Mac client drives will appear as
                                                \\Client\Drive letter. You can now navigate into the
                                                Mac client drives to locate a file to upload.
                                            </span>
                                            <div>
                                                <img
                                                    src={
                                                        process.env.PUBLIC_URL +
                                                        "/img/image005-mac.png"
                                                    }
                                                    alt=""
                                                />
                                            </div>
                                        </li>
                                    </ol>
                                </div>
                            </div>

                            <T.H3 as="h1">Keyboard Mapping</T.H3>
                            <div>
                                Note: Keyboard Mapping may vary depending on the type of Mac
                                Keyboard you have. The following are suggested settings that should
                                work with most Mac Keyboards
                            </div>

                            <div className="group">
                                <T.H4 as="h2">Function Keys</T.H4>
                                <div>
                                    The F1-F12 keys will work by default. Remember to hold down the
                                    Fn key before hitting the F keys. For example: Fn+F2 = GOVT
                                </div>
                            </div>

                            <div className="sub-section">
                                <div>
                                    The "fn" key is always on the bottom left corner. Here are some
                                    other useful shortcuts.
                                </div>
                                <div className="sub-section-content">
                                    <div className="sub-section-group">
                                        <div>fn+end (right arrow) = Menu</div>
                                        <div>fn+page (up arrow) = Page Back</div>
                                        <div>fn+page (down arrow) = Page Forward</div>
                                    </div>
                                </div>
                            </div>

                            <div className="group">
                                Apple keyboard shortcuts may vary depending on the hardware. If you
                                are unable to find a shortcut for a Bloomberg key, you can create a
                                toolbar button (colored keys at the top of your Bloomberg Panel).
                                Press the Apple key (command)+ ALT + G, to see all toolbar buttons.
                                Once the Bloomberg toolbar keys appear, highlight the desired key
                                and click on “Add to End of Toolbar” and the selected button will
                                appear on the top of your Bloomberg Panel.
                            </div>
                        </T.BodyLarge>
                    </div>

                    <div id="section_linux" className="help-section">
                        <T.H2 as="div" className="help-section-title">
                            Linux
                        </T.H2>
                        <T.BodyLarge as="div" className="help-section-content">
                            <T.H3 as="h1">System Requirements</T.H3>
                            <div className="sub-section-content">
                                <div className="group sub-section-group">
                                    <ul>
                                        <li>
                                            At least 55 MB of free disk space for the installed
                                            version of Workspace App and at least 110 MB if you
                                            expand the installation package on the disk.
                                        </li>
                                        <li>TCP/IP networking</li>
                                        <li>
                                            Citrix Workspace App, which can be installed{" "}
                                            <a href="/downloads" target="_blank">
                                                here
                                            </a>
                                        </li>
                                    </ul>
                                </div>
                                <div className="group sub-section-group">
                                    <T.BodyLargeBold as="h5">
                                        For the self-service user interface (UI):
                                    </T.BodyLargeBold>
                                    <ul>
                                        <li>webkit2gtk 2.16.6 or later</li>
                                        <li>libxml2 2.7.8</li>
                                        <li>libxerces-c 3.1</li>
                                    </ul>
                                </div>
                                <div className="group sub-section-group">
                                    <T.BodyLargeBold as="h5">Libraries:</T.BodyLargeBold>
                                    <ul>
                                        <li>glibcxx 3.4.25 or later</li>
                                        <li>glibc 2.27 or later</li>
                                        <li>gtk 3</li>
                                        <li>gtk 2.20.1 or later</li>
                                        <li>libcap1 or libcap2</li>
                                        <li>libva2</li>
                                        <li>libjson-c (for instrumentation)</li>
                                        <li>X11 or X.Org (Wayland isn't supported)</li>
                                        <li>udev support</li>
                                        <li>ALSA (libasound2)</li>
                                        <li>PulseAudio</li>
                                        <li>UIDialogLib3.so</li>
                                    </ul>
                                </div>
                                <div className="group sub-section-group">
                                    <T.BodyLargeBold as="h5">Codec libraries:</T.BodyLargeBold>
                                    <ul>
                                        <li>Speex</li>
                                        <li>Vorbis</li>
                                    </ul>
                                </div>
                                <div className="group sub-section-group">
                                    <T.BodyLargeBold as="h5">
                                        Red Hat Package Manager (RPM) based distributions:
                                    </T.BodyLargeBold>
                                    <ul>
                                        <li>chkconfig</li>
                                    </ul>
                                </div>
                            </div>
                        </T.BodyLarge>
                    </div>

                    <div id="section_faq" className="help-section">
                        <T.H2 as="div" className="help-section-title">
                            Frequently asked questions
                        </T.H2>
                        <T.BodyLarge as="div" className="help-section-content">
                            <div className="group">
                                <T.H4 as="h2">
                                    Are there any limitations for a Bloomberg Anywhere user when
                                    using the website login as opposed to a full Windows
                                    installation?
                                </T.H4>
                                <div>
                                    There are some limitations with using Bloomberg Anywhere via the
                                    web such as limited trading system functionality. For a full
                                    list, please contact{" "}
                                    <a
                                        className="white-link underline-link"
                                        href="https://www.bloomberg.com/professional/customer-support/"
                                    >
                                        Bloomberg Technical Support
                                    </a>
                                    .
                                </div>
                            </div>

                            <div className="group">
                                <T.H4 as="h2">
                                    I’m being asked for a password when installing Citrix on my Mac.
                                    What password is this?
                                </T.H4>
                                <div>
                                    This is typically your Mac login password, or the password you
                                    originally setup for downloads on your Mac. If you have any
                                    difficulty with the password, please contact Apple on
                                    1800-MYAPPLE.
                                </div>
                            </div>

                            <div className="group">
                                <T.H4 as="h2">Can I install the Bloomberg Software on my Mac?</T.H4>
                                <div>
                                    Unfortunately, there is no version for Mac. If you are running
                                    Windows on your Mac, then you will be able to install the
                                    software.
                                </div>
                            </div>

                            <div className="group">
                                <T.H4 as="h2">
                                    If I step away from the computer for some time, my Bloomberg
                                    disconnects, why does this happen?
                                </T.H4>

                                <div>
                                    Bloomberg will automatically disconnect after 60 minutes of
                                    inactivity. If you are experiencing disconnect issues before 60
                                    minutes of inactivity, please check the power options on your
                                    computer.
                                </div>
                            </div>
                        </T.BodyLarge>
                    </div>

                    <div id="section_bunit" className="help-section">
                        <T.H2 as="div" className="help-section-title">
                            B-Unit enroll
                        </T.H2>
                        <T.BodyLarge as="div" className="help-section-content">
                            <T.H3 as="h1">Enroll B-Unit App</T.H3>

                            <div className="group">
                                <Stack direction="row">
                                    <div>
                                        <img
                                            src={process.env.PUBLIC_URL + "/img/image090.png"}
                                            width="400"
                                            alt=""
                                        />
                                    </div>
                                    <div>
                                        <img
                                            src={process.env.PUBLIC_URL + "/img/image091.png"}
                                            width="400"
                                            alt=""
                                        />
                                    </div>
                                </Stack>
                                <div>
                                    <img
                                        src={process.env.PUBLIC_URL + "/img/image092.png"}
                                        width="400"
                                        alt=""
                                    />
                                </div>
                                <div>
                                    <img
                                        src={process.env.PUBLIC_URL + "/img/image093.png"}
                                        alt=""
                                    />
                                </div>
                            </div>
                            <T.H3 as="h1">Enroll B-Unit Device</T.H3>
                            <div className="group">
                                <Stack direction="row">
                                    <div>
                                        <img
                                            src={process.env.PUBLIC_URL + "/img/image090.png"}
                                            width="400"
                                            alt=""
                                        />
                                    </div>
                                    <div>
                                        <img
                                            src={process.env.PUBLIC_URL + "/img/image094.png"}
                                            width="400"
                                            alt=""
                                        />
                                    </div>
                                </Stack>
                                <Stack direction="row">
                                    <div>
                                        <img
                                            src={process.env.PUBLIC_URL + "/img/image095.png"}
                                            width="400"
                                            alt=""
                                        />
                                    </div>
                                    <div>
                                        <img
                                            src={process.env.PUBLIC_URL + "/img/image096.png"}
                                            width="400"
                                            alt=""
                                        />
                                    </div>
                                </Stack>
                                <Stack direction="row">
                                    <div>
                                        <img
                                            src={process.env.PUBLIC_URL + "/img/image097.png"}
                                            width="400"
                                            alt=""
                                        />
                                    </div>
                                    <div>
                                        <img
                                            src={process.env.PUBLIC_URL + "/img/image098.png"}
                                            width="400"
                                            alt=""
                                        />
                                    </div>
                                </Stack>
                                <div>
                                    <img
                                        src={process.env.PUBLIC_URL + "/img/image099.png"}
                                        width="400"
                                        alt=""
                                    />
                                </div>
                            </div>
                        </T.BodyLarge>
                    </div>
                    <div id="section_files" className="help-section">
                        <T.H2 as="div" className="help-section-title">
                            Accessing files on your local computer
                        </T.H2>
                        <T.BodyLarge as="div" className="help-section-content">
                            <div className="group question-pair">
                                <T.H4 as="h2" id="drive_access_citrix">
                                    How do I access files on Citrix?
                                </T.H4>
                                <div>
                                    <ol className="numeric-list">
                                        <li>
                                            <span>Open Citrix Workspace app.</span>
                                        </li>
                                        <li>
                                            <span>
                                                Click the account icon in the top‑right corner and
                                                click the Accounts option.
                                            </span>
                                        </li>
                                        <li>
                                            <span>
                                                Go to the “File access” to select a mapped drive
                                            </span>
                                        </li>

                                        <li>
                                            <span>
                                                Select “Read and Write” or “Ask me each time”
                                            </span>
                                        </li>
                                        <li>
                                            <span>
                                                Click “Add” to specify the location and select a
                                                drive to map to it
                                            </span>
                                            <div>
                                                <img
                                                    src={
                                                        process.env.PUBLIC_URL + "/img/image066.png"
                                                    }
                                                    width="800"
                                                    alt=""
                                                />
                                            </div>
                                        </li>
                                    </ol>
                                </div>
                            </div>
                        </T.BodyLarge>
                    </div>

                    <div id="section_excel" className="help-section">
                        <T.H2 as="div" className="help-section-title">
                            Using Bloomberg Excel add-In
                        </T.H2>
                        <T.BodyLarge as="div" className="help-section-content">
                            <div className="group question-pair">
                                <T.H4 as="h2" id="api_excel_whatis">
                                    What is Bloomberg Excel Add-In with BLOOMBERG ANYWHERE?
                                </T.H4>
                                <div>
                                    Bloomberg Excel Add-In with BLOOMBERG ANYWHERE allows you to use
                                    a remote version of Microsoft Excel running on Bloomberg
                                    servers. This is a fully functional copy of Microsoft Excel that
                                    you can use to interact with Bloomberg API data.
                                </div>
                            </div>

                            <div className="group question-pair">
                                <T.H4 as="h2" id="api_excel_install_locally">
                                    Do I need Microsoft Excel to be installed locally?
                                </T.H4>
                                <div>
                                    No, you don’t need to install Microsoft Excel on your computer
                                    to use Bloomberg Excel Add-In. The spreadsheets are opened and
                                    saved using a remote version of Excel.
                                </div>
                            </div>

                            <div className="group question-pair">
                                <T.H4 as="h2" id="api_excel_new_functionality">
                                    What can I do with this new functionality?
                                </T.H4>
                                <div className="sub-section-content">
                                    <ol>
                                        <li>
                                            <a className="faq_answer_link" href="#api_excel_in_msg">
                                                Open a spreadsheet with API data that is attached to
                                                MSG
                                            </a>
                                        </li>
                                        <li>
                                            Open a spreadsheet stored on your local{" "}
                                            <a className="faq_answer_link" href="#api_excel">
                                                computer
                                            </a>
                                        </li>
                                        <li>
                                            Allow Bloomberg Anywhere to access files on your{" "}
                                            <a
                                                className="faq_answer_link"
                                                href="#drive_access_windows"
                                            >
                                                Windows
                                            </a>
                                            ,
                                            <a className="faq_answer_link" href="#drive_access_mac">
                                                Mac
                                            </a>
                                        </li>
                                        <li>
                                            <a
                                                className="faq_answer_link"
                                                href="#api_excel_under_function"
                                            >
                                                Open a spreadsheet under a Bloomberg function. For
                                                example XLTP
                                            </a>
                                        </li>
                                        <li>
                                            Save a spreadsheet to your local{" "}
                                            <a
                                                className="faq_answer_link"
                                                href="#api_excel_sheet_in_file"
                                            >
                                                Windows
                                            </a>{" "}
                                            computer or{" "}
                                            <a
                                                className="faq_answer_link"
                                                href="#api_excel_sheet_in_file"
                                            >
                                                Mac
                                            </a>
                                        </li>
                                    </ol>
                                </div>
                            </div>

                            <div className="group question-pair">
                                <T.H4 as="h2" id="api_excel_in_msg">
                                    How do I open an Excel spreadsheet in Bloomberg Message (MSG)?
                                </T.H4>
                                <div className="sub-section-content">
                                    <ol>
                                        <li>
                                            Open the Message with the attached Excel spreadsheet
                                            file
                                        </li>
                                        <div>
                                            <img
                                                src={
                                                    process.env.PUBLIC_URL +
                                                    "/img/image001-excel.png"
                                                }
                                                alt=""
                                            />
                                        </div>
                                        <li>
                                            Click on the name of the attached file that you would
                                            like to open. In the example below, the name of the
                                            attached file is test.xls.
                                        </li>
                                        <div>
                                            <img
                                                src={
                                                    process.env.PUBLIC_URL +
                                                    "/img/image000-excel.png"
                                                }
                                                alt=""
                                            />
                                        </div>
                                        <li>
                                            You can then select to save this file locally or open it
                                            within the Citrix Session
                                        </li>
                                    </ol>
                                    <p className="no-margin-bottom">
                                        The attached file might take some time to open based on its
                                        size.
                                    </p>
                                </div>
                            </div>

                            <div className="group question-pair">
                                <T.H4 as="h2" id="api_excel">
                                    How do I open an Excel spreadsheet that is on my computer?
                                </T.H4>
                                <div className="sub-section-content">
                                    <ol className="numeric-list">
                                        <li>
                                            <span>
                                                Ensure that BLOOMBERG ANYWHERE can access files on
                                                your computer
                                            </span>
                                            <div>
                                                <ul>
                                                    <li>
                                                        <a
                                                            className="faq_answer_link"
                                                            href="#drive_access_windows"
                                                        >
                                                            How to check and enable drive access on
                                                            a Windows PC
                                                        </a>
                                                    </li>
                                                    <li>
                                                        <a
                                                            className="faq_answer_link"
                                                            href="#drive_access_mac"
                                                        >
                                                            How to check and enable drive access on
                                                            a Mac
                                                        </a>
                                                    </li>
                                                </ul>
                                            </div>
                                        </li>

                                        <li>
                                            <span>
                                                Upload the spreadsheet from your computer to
                                                BLOOMBERG ANYWHERE
                                            </span>
                                            <div>
                                                <ol className="alpha-list">
                                                    <li>
                                                        From a BLOOMBERG ANYWHERE window, run FILE
                                                        &lt;GO&gt;
                                                    </li>
                                                    <li>
                                                        <span>Click on the Upload File button</span>
                                                        <div>
                                                            <img
                                                                src={
                                                                    process.env.PUBLIC_URL +
                                                                    "/img/image005-excel.png"
                                                                }
                                                                alt=""
                                                            />
                                                        </div>
                                                    </li>
                                                    <li>
                                                        <span>Expand the Shared Drives folder</span>
                                                        <div>
                                                            <img
                                                                src={
                                                                    process.env.PUBLIC_URL +
                                                                    "/img/image007-excel.png"
                                                                }
                                                                alt=""
                                                            />
                                                        </div>
                                                    </li>
                                                    <li>
                                                        <span>
                                                            Expand the relevant folder for the drive
                                                            on which the file that you would like to
                                                            upload is located
                                                        </span>
                                                        <div>
                                                            <ul>
                                                                <li>
                                                                    For example, expand \\Client\C$
                                                                    if you would like to browse to
                                                                    your C:\
                                                                </li>
                                                            </ul>
                                                        </div>
                                                    </li>

                                                    <li>
                                                        Browse to the location of the spreadsheet
                                                    </li>
                                                    <li>
                                                        Select the spreadsheet that you would like
                                                        to Open
                                                    </li>
                                                    <li>
                                                        <span>Click on the Upload file button</span>
                                                        <div>
                                                            <img
                                                                src={
                                                                    process.env.PUBLIC_URL +
                                                                    "/img/image009.png"
                                                                }
                                                                alt=""
                                                            />
                                                        </div>
                                                    </li>

                                                    <li>
                                                        <span>
                                                            Upon the file upload completion, you
                                                            should get the message “Document *.*
                                                            upload complete” as shown below
                                                        </span>
                                                        <div>
                                                            <img
                                                                src={
                                                                    process.env.PUBLIC_URL +
                                                                    "/img/image011.png"
                                                                }
                                                                alt=""
                                                            />
                                                        </div>
                                                    </li>
                                                    <li>
                                                        Open the uploaded file from FILE &lt;GO&gt;
                                                        to use it within Bloomberg Anywhere
                                                    </li>
                                                </ol>
                                            </div>
                                        </li>
                                    </ol>
                                </div>
                            </div>

                            <div className="group question-pair">
                                <T.H4 as="h2" id="api_excel_under_function">
                                    How do I open a spreadsheet under a Bloomberg function, for
                                    example XLTP &lt;GO&gt;?
                                </T.H4>
                                <div>
                                    <p className="no-margin-top">
                                        You can open up a Spreadsheet from under a Bloomberg
                                        function. Just one example of doing so is by using XLTP
                                        &lt;GO&gt;
                                    </p>
                                    <div>
                                        <ol className="numeric-list">
                                            <li>Run XLTP &lt;GO&gt;</li>
                                            <li>
                                                <span>
                                                    Click on the Open button to the lower right hand
                                                    corner of the screen to open up a spreadsheet
                                                </span>
                                                <div>
                                                    <img
                                                        src={
                                                            process.env.PUBLIC_URL +
                                                            "/img/image015.png"
                                                        }
                                                        alt=""
                                                    />
                                                </div>
                                            </li>
                                        </ol>
                                    </div>
                                </div>
                            </div>

                            <div className="group question-pair">
                                <T.H4 as="h2" id="api_excel_sheet_in_file">
                                    How do I open a spreadsheet in FILE/PFM &lt;GO&gt; from within
                                    the remote Excel?
                                </T.H4>
                                <div>
                                    <ol className="numeric-list">
                                        <li>
                                            From within the remote Excel, click on the Bloomberg tab
                                        </li>
                                        <li>Click on the File Manager button</li>
                                        <li>
                                            <span>Select the Open option</span>x
                                            <div>
                                                <img
                                                    src={
                                                        process.env.PUBLIC_URL + "/img/image022.png"
                                                    }
                                                    alt=""
                                                />
                                            </div>
                                        </li>
                                        <li>
                                            <span>
                                                From within the Bloomberg File Manager, select the
                                                spreadsheet that you would like to open
                                            </span>
                                            <div>
                                                <img
                                                    src={
                                                        process.env.PUBLIC_URL + "/img/image024.png"
                                                    }
                                                    alt=""
                                                />
                                            </div>
                                        </li>
                                        <li>
                                            <span>Click on the Open button</span>
                                            <p className="no-margin-top">
                                                The file will be downloaded then opened in the
                                                Remote Excel
                                            </p>
                                        </li>
                                    </ol>
                                </div>
                            </div>

                            <div className="group question-pair">
                                <T.H4 as="h2" id="api_excel_using_remote">
                                    How can I tell whether I am using remote Excel on BLOOMBERG
                                    ANYWHERE?
                                </T.H4>
                                <div>
                                    <p>
                                        One of the ways to tell whether you are likely using the
                                        remote version of Excel is by checking to see if the author
                                        is AgentUser{" "}
                                    </p>
                                    <div>
                                        <img
                                            src={process.env.PUBLIC_URL + "/img/image036.png"}
                                            alt=""
                                        />
                                    </div>
                                </div>
                            </div>
                        </T.BodyLarge>
                    </div>

                    <div id="section_html5" className="help-section">
                        <T.H2 as="div" className="help-section-title">
                            Launch with Browser
                        </T.H2>
                        <T.BodyLarge as="div" className="help-section-content">
                            <div className="group question-pair">
                                <T.H3 as="h1" id="html5_legacy_fallback">
                                    I&apos;m having trouble with the new &quot;Launch with
                                    Browser&quot;. Is there still a way to fall back to the legacy
                                    experience?
                                </T.H3>
                                <div>
                                    Yes, the legacy experience will be temporarily available as a
                                    fallback for users who are experiencing critical issues with
                                    &quot;Launch with Browser&quot;.
                                </div>
                                <div>
                                    To go back to the legacy experience, use the dropdown arrow next
                                    to &quot;Launch&quot; in BBA and select &quot;Launch with
                                    Browser (Legacy)&quot;.
                                </div>
                                <div>
                                    <img
                                        src={process.env.PUBLIC_URL + "/img/image080.png"}
                                        alt=""
                                    />
                                </div>
                            </div>

                            <T.H3 as="h1">Drive Mapping</T.H3>
                            <ContextualMessage kind="warning">
                                NOTE: Drive mapping isn&apos;t yet supported in Safari.
                            </ContextualMessage>
                            <div>
                                The Browser Client will prompt you to map any of your local folders,
                                to make those folders available for upload/download, when you try to
                                attach a file for the first time or use file sharing features of the
                                Terminal.
                            </div>
                            <div>
                                If you didn&apos;t see such a prompt, you can also manually map
                                folders by clicking the drive icon in the browser client&apos;s
                                taskbar and choosing the local folders that contain your files.
                            </div>
                            <div>
                                <img src={process.env.PUBLIC_URL + "/img/image081.png"} alt="" />
                            </div>
                            <div>
                                <img src={process.env.PUBLIC_URL + "/img/image082.png"} alt="" />
                            </div>
                            <div>
                                Once a folder is mapped, you can access it through Shared Drives
                                &gt; tsclient/Drives.
                            </div>
                            <div>
                                On macOS computers, certain system folders, such as your
                                &quot;Downloads&quot; or &quot;Documents&quot; folders, cannot be
                                shared due to Mac security policy.
                            </div>

                            <T.H3 as="h1">Playing Sound from Bloomberg Anywhere</T.H3>
                            <div>
                                You will hear sound from the Bloomberg Terminal after you first
                                interact with the session. If sound is not yet enabled, you will see
                                a red &quot;Speaker&quot; icon in the bottom-right of the browser
                                client&apos;s taskbar.
                            </div>

                            <T.H3 as="h1">Using Your Local Keyboard Layout / IME Editing</T.H3>
                            <div>
                                When you first connect using &quot;Launch with Browser&quot;, the
                                system will detect your keyboard layout:
                            </div>
                            <div>
                                <img
                                    className="keyboard-layout-img"
                                    src={process.env.PUBLIC_URL + "/img/image087.png"}
                                    alt=""
                                />
                            </div>
                            <div>
                                If you would like to change your typing language while you are
                                working inside Bloomberg Anywhere:
                            </div>
                            <ul>
                                <li>
                                    If you have multiple languages installed on your computer,
                                    switch the keyboard layout using your standard operating system
                                    menu, or the &quot;Keyboard&quot; icon in the browser
                                    client&apos;s taskbar (see the steps below).
                                </li>
                                <li>
                                    If you need to type in a language that uses an IME, such as
                                    Simplified Chinese, Japanese, or Korean, select it using the
                                    &quot;Keyboard&quot; icon in the browser client&apos;s taskbar
                                    (see the steps below).
                                </li>
                                <li>
                                    Changing the language via the in-session language bar will not
                                    work. Change it via the browser client&apos;s taskbar instead.
                                </li>
                                <li>
                                    For a British keyboard on macOS, the &quot;British&quot;
                                    keyboard is not automatically detected. Either use the
                                    &quot;British - PC&quot; keyboard rather than the
                                    &quot;British&quot; keyboard, or change the keyboard to
                                    &quot;English (UK)&quot; in the browser client&apos;s taskbar.
                                </li>
                            </ul>
                            <div>To change the language using the &quot;Keyboard&quot; icon:</div>
                            <ol>
                                <li>
                                    Click the &quot;Keyboard&quot; icon in the bottom-right of the
                                    browser client&apos;s taskbar.
                                    <div>
                                        <img
                                            src={process.env.PUBLIC_URL + "/img/image089.png"}
                                            alt=""
                                        />
                                    </div>
                                </li>
                                <li>
                                    In the &quot;Keyboard layout&quot; dialog, select your language,
                                    for example 中国简体 (Chinese Simplified).
                                    <div>
                                        <img
                                            className="keyboard-layout-img"
                                            src={process.env.PUBLIC_URL + "/img/image088.png"}
                                            alt=""
                                        />
                                    </div>
                                </li>
                                <li>Click &quot;Change&quot;.</li>
                            </ol>

                            <T.H3 as="h1">Microphone Support</T.H3>
                            <div>
                                To use a microphone, enable microphone access in your browser.
                            </div>
                            <div>
                                <img src={process.env.PUBLIC_URL + "/img/image086.png"} alt="" />
                            </div>

                            <T.H3 as="h1">Mac</T.H3>

                            <T.H4 as="h2">Safari</T.H4>
                            <div>Safari has the following limitations:</div>
                            <ul className="safari-limitations">
                                <li>
                                    <T.BodyLargeBold as="strong">Drive mapping</T.BodyLargeBold> —
                                    Not supported. Switch to another browser or fall back to the
                                    legacy experience.
                                </li>
                                <li>
                                    <T.BodyLargeBold as="strong">Copy and paste</T.BodyLargeBold> —
                                    To copy and paste text between your computer and the session,
                                    use the Remote Clipboard in the bottom-right of the browser
                                    client&apos;s taskbar.
                                    <div>
                                        <img
                                            className="remote-clipboard-img"
                                            src={process.env.PUBLIC_URL + "/img/image085.png"}
                                            alt=""
                                        />
                                    </div>
                                </li>
                                <li>
                                    <T.BodyLargeBold as="strong">URL redirection</T.BodyLargeBold> —
                                    See{" "}
                                    <a className="faq_answer_link" href="#html5_popup_blocked">
                                        Why do I receive a &quot;Popup Blocked&quot; message when I
                                        launch a URL in the Bloomberg Terminal?
                                    </a>{" "}
                                    below for a workaround.
                                </li>
                            </ul>

                            <T.H4 as="h2" id="html5_popup_blocked">
                                Why do I receive a &quot;Popup Blocked&quot; message when I launch a
                                URL in the Bloomberg Terminal?
                            </T.H4>
                            <div>
                                <img
                                    className="popup-blocked-img"
                                    src={process.env.PUBLIC_URL + "/img/image084.png"}
                                    alt=""
                                />
                            </div>
                            <div>
                                To solve this issue, from the Safari menu, select Settings for
                                &quot;_____.blpprofessional.com&quot;, then set Pop-up Windows to
                                Allow as shown in the screenshot below.
                            </div>
                            <div>
                                <img
                                    className="popup-blocked-img"
                                    src={process.env.PUBLIC_URL + "/img/image083.png"}
                                    alt=""
                                />
                            </div>
                        </T.BodyLarge>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Help;
